import * as z from "zod/v4";

const portalBrandingSchema = z.object({
  logoUrl: z.string().nullish(),
  faviconUrl: z.string().nullish(),
  backgroundColor: z.string().nullish(),
  backgroundImageUrl: z.union([z.string().url(), z.literal("")]).nullish(),
});

const portalColorsSchema = z.object({
  primary: z.string().nullish(),
  background: z.string().nullish(),
  error: z.string().nullish(),
});

const portalNavbarSchema = z.object({
  backgroundColor: z.string().nullish(),
  textColor: z.string().nullish(),
});

const portalTypographyFontsSchema = z.object({
  heading: z.string().nullish(),
  body: z.string().nullish(),
  custom: z.array(z.string()).nullish(),
});

const portalTextColorsSchema = z.object({
  primary: z.string().nullish(),
  secondary: z.string().nullish(),
});

const portalTypographySchema = z.object({
  fonts: portalTypographyFontsSchema.nullish(),
  textColors: portalTextColorsSchema.nullish(),
});

const portalButtonColorsSchema = z.object({
  background: z.string().nullish(),
  border: z.string().nullish(),
  text: z.string().nullish(),
  backgroundHover: z.string().nullish(),
  textHover: z.string().nullish(),
});

const portalButtonStyleSchema = z.object({
  font: z.string().nullish(),
  cornerRadius: z.number().nullish(),
  borderWidth: z.number().nullish(),
  padding: z.number().nullish(),
});

const portalButtonVariantSchema = z.object({
  colors: portalButtonColorsSchema.nullish(),
  style: portalButtonStyleSchema.nullish(),
});

const portalButtonsSchema = z.object({
  primary: portalButtonVariantSchema.nullish(),
  secondary: portalButtonVariantSchema.nullish(),
  tertiary: portalButtonVariantSchema.nullish(),
});

const portalInputFieldColorsSchema = z.object({
  background: z.string().nullish(),
  border: z.string().nullish(),
  text: z.string().nullish(),
  error: z.string().nullish(),
});

const portalInputFieldStyleSchema = z.object({
  font: z.string().nullish(),
  cornerRadius: z.number().nullish(),
  borderWidth: z.number().nullish(),
  padding: z.number().nullish(),
});

const portalInputFieldsSchema = z.object({
  colors: portalInputFieldColorsSchema.nullish(),
  style: portalInputFieldStyleSchema.nullish(),
});

const portalSelectionCardColorsSchema = z.object({
  selectedBackground: z.string().nullish(),
  selectedBorder: z.string().nullish(),
});

const portalStyleSchema = z.object({
  cornerRadius: z.number().nullish(),
  borderWidth: z.number().nullish(),
  colors: portalSelectionCardColorsSchema.nullish(),
});

const loginPageCustomizationSchema = z.object({
  colors: z
    .object({
      backToStoreText: z.string().nullish(),
      headingText: z.string().nullish(),
      bodyText: z.string().nullish(),
    })
    .nullish(),
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      emailLabel: z.string().nullish(),
      orderNumberLabel: z.string().nullish(),
      loginButton: z.string().nullish(),
      giftButton: z.string().nullish(),
      returnPolicyLink: z.string().nullish(),
      registrationTitle: z.string().nullish(),
    })
    .nullish(),
  branding: z
    .object({
      backgroundImage: z.string().nullish(),
      cardBackground: z.string().nullish(),
      cardOpacity: z.number().min(0).max(1).nullish(),
      cardBlur: z.number().min(0).max(40).nullish(),
    })
    .nullish(),
  behavior: z
    .object({
      hideGiftButton: z.boolean().nullish(),
      hideWarrantyRegistration: z.boolean().nullish(),
      hideEmailLogin: z.boolean().nullish(),
    })
    .nullish(),
  alertHtml: z.string().nullish(),
});

const orderDetailsPageCustomizationSchema = z.object({
  text: z
    .object({
      returnButton: z.string().nullish(),
      registerButton: z.string().nullish(),
      warrantyClaimButton: z.string().nullish(),
      shippingClaimButton: z.string().nullish(),
      returnStatusSubtitle: z.string().nullish(),
      exchangeStatusSubtitle: z.string().nullish(),
      storeCreditStatusSubtitle: z.string().nullish(),
    })
    .nullish(),
  behavior: z
    .object({ hideStartReturnButton: z.boolean().nullish() })
    .nullish(),
});

const selectItemsPageCustomizationSchema = z.object({
  text: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
});

const itemDetailsPageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      addDetailsButton: z.string().nullish(),
      editDetailsButton: z.string().nullish(),
    })
    .nullish(),
  behavior: z
    .object({ hideAddExchangeItemButton: z.boolean().nullish() })
    .nullish(),
});

const returnTypePageCustomizationSchema = z.object({
  text: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      sameItemTitle: z.string().nullish(),
      sameItemSubtitle: z.string().nullish(),
      variantExchangeTitle: z.string().nullish(),
      variantExchangeSubtitle: z.string().nullish(),
      exchangeGroupsTitle: z.string().nullish(),
      exchangeGroupsSubtitle: z.string().nullish(),
      notExchangeButtonText: z.string().nullish(),
      notExchangeButtonSubtitle: z.string().nullish(),
    })
    .nullish(),
  backorder: z
    .object({
      heading: z.string().nullish(),
      subtitle: z.string().nullish(),
      showDate: z.boolean().nullish(),
    })
    .nullish(),
});

const shipmentMethodTitlesSchema = z.object({
  inStore: z.string().nullish(),
  selfShip: z.string().nullish(),
  blueYonder: z.string().nullish(),
  returnBear: z.string().nullish(),
  manualLabel: z.string().nullish(),
  dropoff: z.string().nullish(),
});

const shipmentMethodSubtitlesSchema = z.object({
  inStore: z.string().nullish(),
  selfShip: z.string().nullish(),
  blueYonder: z.string().nullish(),
  returnBear: z.string().nullish(),
  manualLabel: z.string().nullish(),
});

const shipmentMethodPageCustomizationSchema = z.object({
  text: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  methodTitles: shipmentMethodTitlesSchema.nullish(),
  methodSubtitles: shipmentMethodSubtitlesSchema.nullish(),
});

const confirmationStepTextSchema = z.object({
  title: z.string().nullish(),
  subtitle: z.string().nullish(),
});

const confirmationPageCustomizationSchema = z.object({
  text: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  draftOrder: z
    .object({ heading: z.string().nullish(), subtitle: z.string().nullish() })
    .nullish(),
  steps: z
    .object({
      title: z.string().nullish(),
      manualReview: confirmationStepTextSchema.nullish(),
      inTransit: confirmationStepTextSchema.nullish(),
      processed: confirmationStepTextSchema.nullish(),
      processedRepair: confirmationStepTextSchema.nullish(),
      dropOffPackage: confirmationStepTextSchema.nullish(),
    })
    .nullish(),
});

const reviewPageCustomizationSchema = z.object({
  text: z
    .object({
      pickupTitle: z.string().nullish(),
      pickupSubtext: z.string().nullish(),
      inStoreTitle: z.string().nullish(),
      inStoreSubtext: z.string().nullish(),
      blueYonderTitle: z.string().nullish(),
      blueYonderSubtext: z.string().nullish(),
      selfShipTitle: z.string().nullish(),
      selfShipSubtext: z.string().nullish(),
      manualLabelTitle: z.string().nullish(),
      manualLabelSubtext: z.string().nullish(),
      itemsOldHeader: z.string().nullish(),
      itemsNewHeader: z.string().nullish(),
      standardExchangeSubHeader: z.string().nullish(),
      instantExchangeSubText: z.string().nullish(),
      adjustmentPositiveName: z.string().nullish(),
      adjustmentNegativeName: z.string().nullish(),
    })
    .nullish(),
  behavior: z.object({ showDiscountCodes: z.boolean().nullish() }).nullish(),
});

const compensationMethodPageCustomizationSchema = z.object({
  behavior: z
    .object({ showSuggestedProducts: z.boolean().nullish() })
    .nullish(),
});

const portalPagesCustomizationSchema = z.object({
  login: loginPageCustomizationSchema.nullish(),
  orderDetails: orderDetailsPageCustomizationSchema.nullish(),
  selectItems: selectItemsPageCustomizationSchema.nullish(),
  itemDetails: itemDetailsPageCustomizationSchema.nullish(),
  returnType: returnTypePageCustomizationSchema.nullish(),
  shipmentMethod: shipmentMethodPageCustomizationSchema.nullish(),
  review: reviewPageCustomizationSchema.nullish(),
  confirmation: confirmationPageCustomizationSchema.nullish(),
  compensationMethod: compensationMethodPageCustomizationSchema.nullish(),
});

export const portalCustomizationZodSchema = z.object({
  branding: portalBrandingSchema.nullish(),
  colors: portalColorsSchema.nullish(),
  navbar: portalNavbarSchema.nullish(),
  typography: portalTypographySchema.nullish(),
  buttons: portalButtonsSchema.nullish(),
  inputFields: portalInputFieldsSchema.nullish(),
  style: portalStyleSchema.nullish(),
  pages: portalPagesCustomizationSchema.nullish(),
});

export type PortalCustomization = z.infer<typeof portalCustomizationZodSchema>;

export const getPortalCustomization = {
  name: "getPortalCustomization" as const,
  input: z.object({}),
  output: z.object({ customization: portalCustomizationZodSchema.nullable() }),
};
